import { useContext, useState } from 'react';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useCookie } from 'next-cookie';
import { Button, ButtonsContainer, Modal, Quantity, ThemeContext, Tooltip } from '@forma/forma-ui-kit';
import ClientWrap from '@/components/ClientWrap';
import RequestDemoModal3 from '@/components/blocks/RequestDemoModal3';
import RequestDemoModal2 from '@/components/blocks/RequestDemoModal2';
import { analytics } from '@/helpers/analytics';
import formatPrice from '@/helpers/formatPrice';

import styles from './tariffs-item.module.css';

const BUSINESS_TARIFF_ID = 'tariff_business';
const USERS_INDEX = 2;
const ATTACHMENTS_INDEX = 1;
// const lkUrl = process.env.LK_URL;

const advantagesItems = {
  tariff_team: [
    'Перенос и настройка до <b>1-го</b> шаблона',
    'Хранить до <b>1 ГБ</b> документов в модуле «Электронный архив»',
    'До <b>3-х</b> активных пользователей'
  ],
  tariff_business: [
    'Перенос и настройка до <b>2-х</b> шаблонов',
    'Хранить до <b>3 ГБ</b> документов в модуле «Электронный архив»',
    'До <b>6-ти</b> активных пользователей',
    'Персональное обучение Pro'
  ],
  tariff_enterprise: [
    'Перенос и настройка до <b>5-ти</b> шаблонов ',
    'Хранить до <b>5 ГБ</b> документов в модуле «Электронный архив»',
    'До <b>15-ти</b> активных пользователей',
    'Персональное обучение Pro',
    'Персональный менеджер Pro'
  ],
};

const includesItems = [
  { text: 'Доступ к <b>300+</b> шаблонов документов' },
  { text: 'Безлимитное ∞ создание документов' },
  { text: 'Создание комплекта документов' },
  { text: 'Моментальный экспорт в doc. и pdf.' },
  { text: 'Отправка на заполнение по ссылке' },
  { text: 'Дистанционное подписание с ПЭП' },
  { text: 'Интеграция с AmoCRM и Bitrix24', isNew: true },
  { text: 'Быстрая техническая поддержка' },
  { text: 'Настройка без программистов' },
  { text: 'Настройка ролей и прав сотрудников' },
  { text: 'Доступ к расширенной базе знаний' },
  { text: 'Мгновенный доступ к сервису' },
];

const TariffIncludes = () => {
  return (
    <div className={styles.includes}>
      <div className={styles.includesTitle}>
        Всегда входит в тариф
      </div>
      <ul className={styles.includesItems}>
        {includesItems.map(({ text, isNew }) => (
          <li className={styles.includesItem} key={text}>
            <span dangerouslySetInnerHTML={{ __html: text }} />
            {isNew && <span className={styles.newLabel}>New</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};

const TariffPrices = ({
  price, oldPrice, currency, usersLimit, members, userPrice, attachmentsSize, attachmentsPrice
}) => {
  const { t } = useTranslation();

  let monthOldPrice = oldPrice;
  if (members) monthOldPrice += members * userPrice;
  if (attachmentsSize) monthOldPrice += attachmentsSize * attachmentsPrice;

  let monthPrice = price;
  if (members) monthPrice += members * userPrice;
  if (attachmentsSize) monthPrice += attachmentsSize * attachmentsPrice;

  return (
    <div className={styles.prices}>
      {monthOldPrice !== monthPrice && (
        <div className={styles.oldPrice}>
          {formatPrice(monthOldPrice, currency)} / {t('month')}
        </div>
      )}
      <div className={styles.price}>
        {formatPrice(monthPrice, currency)} / {t('month')}
      </div>
      <div className={styles.priceDuration}>
        {t('for_count_users', { count: usersLimit })}
      </div>
    </div>
  );
};

const TariffsItem = ({
  id, name, description, patterns, canAddUsers, usersLimit, selectedDuration
}) => {
  const cookie = useCookie();
  const { t } = useTranslation();
  const { currencies, viewport } = useContext(ThemeContext);
  const currency = currencies.ru;
  const [members, setMembers] = useState(0);
  const [attachmentsSize, setAttachmentsSize] = useState(0);
  const [isOpenRequest, setOpenRequest] = useState(false);
  const [isOpenDemo, setOpenDemo] = useState(false);

  const pattern = patterns.find(({ duration }) => duration === selectedDuration);
  const durationMonths = pattern ? pattern.duration / 30 : 0;
  const durationBasePrice = pattern ? pattern.basePrice / durationMonths : 0;
  const durationPrice = pattern ? pattern.price / durationMonths : 0;
  const durationUserPrice = (pattern?.userPrice && canAddUsers) ? pattern.userPrice / durationMonths : 0;
  // const maxUserPrice = Math.max(...patterns.map(({ duration, userPrice }) => userPrice ? userPrice / (duration / 30) : 0));
  const durationAttachmentsPrice = pattern ? pattern.attachmentsSizePrice / durationMonths : 0;

  const isPopular = id === BUSINESS_TARIFF_ID;
  const isPhone = viewport === 'phone';

  const handleClickTry = () => {
    const date = new Date();
    cookie.set('tariff', JSON.stringify({
      tariff: id,
      users: members,
      duration: selectedDuration,
      attachments: attachmentsSize,
    }), {
      path: '/',
      expires: new Date(date.setDate(date.getDate() + 7)),
      domain: window.location.hostname
    });

    analytics.clickTryTariff();
  };

  let subtitle = '';
  if (id === 'tariff_team') subtitle = 'Для малого бизнеса';
  if (id === 'tariff_business') subtitle = 'Для среднего бизнеса';
  if (id === 'tariff_enterprise') subtitle = 'Для крупного бизнеса';

  return (
    <div className={classNames(styles.item, isPopular && styles.active)}>
      {isPopular && <div className={styles.popular}>Выбор пользователей</div>}
      <div className={styles.info}>
        <div className={styles.title}>
          {name}
          {/* {!!pattern?.discount && (
            <span className={styles.discountLabel}>-{pattern.discount}%</span>
          )} */}
        </div>
        <div className={styles.subtitle}>
          {subtitle}
          <ClientWrap>
            {isPhone ? (
              <Modal
                control={<div className={styles.question}><ReactSVG src="/icons/question-circle.svg" wrapper="span" /></div>}
                title={subtitle}
                titleClassName={styles.questionModalTitle}
                buttonsClassName={styles.questionModalButtons}
                buttons={[
                  {
                    className: styles.modalButton,
                    viewStyle: 'secondary',
                    children: 'Понятно',
                  },
                ]}
              >
                <div className={styles.questionModal}>
                  {description}
                </div>
              </Modal>
            ) : (
              <Tooltip
                control={<div className={styles.question}><ReactSVG src="/icons/question-circle.svg" wrapper="span" /></div>}
              >
                <div className={styles.questionTooltip}>
                  {description}
                </div>
              </Tooltip>
            )}
          </ClientWrap>
        </div>
        <TariffPrices
          price={durationPrice}
          oldPrice={durationBasePrice}
          currency={currency}
          usersLimit={usersLimit}
          members={members}
          userPrice={durationUserPrice}
          attachmentsSize={attachmentsSize}
          attachmentsPrice={durationAttachmentsPrice}
        />
      </div>

      <ButtonsContainer className={styles.buttons}>
        <Button
          // as={Link}
          viewStyle="primary"
          onClick={() => { handleClickTry(); setOpenRequest(true); }}
          className={classNames(styles.button, styles.primaryButton)}
          // href={`${lkUrl}/register?lng=${lang}`}
          fullWidth
        >
          {t('start_free')}
        </Button>
        <Button
          // as={Link}
          viewStyle="secondary"
          onClick={() => setOpenDemo(true)}
          className={classNames(styles.button, styles.secondaryButton)}
          // href="/request"
          fullWidth
        >
          {t('request_demo')}
        </Button>
        <div className={styles.buttonsNotice}>
          Бесплатно 7 дней. Карта не требуется.
        </div>
      </ButtonsContainer>

      <div className={styles.advantages}>
        {advantagesItems[id]?.map((item, index) => (
          <div className={styles.advantage} key={`${id}_${index}`}>
            <ReactSVG className={styles.advantageIcon} src="/icons/checkmark-circle.svg" />
            <div className={styles.advantageText}>
              <span dangerouslySetInnerHTML={{ __html: item }} />
              {!!(durationUserPrice && index === USERS_INDEX) && (
                <div className={styles.additionalCount}>
                  <div className={styles.additionalDescr}>
                    {t('additional_user_over_tariff', { price: formatPrice(durationUserPrice, currency).replaceAll(' ', ' ') })}
                  </div>
                  <Quantity value={members} onChange={setMembers} />
                </div>
              )}
              {!!(index === ATTACHMENTS_INDEX) && (
                <div className={styles.additionalCount}>
                  <div className={styles.additionalDescr}>
                    {t('place_over_tariff', { price: formatPrice(durationAttachmentsPrice, currency).replaceAll(' ', ' ') })}
                  </div>
                  <Quantity value={attachmentsSize} onChange={setAttachmentsSize} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <TariffIncludes />

      <RequestDemoModal2
        open={isOpenDemo}
        onClose={() => setOpenDemo(false)}
      />

      <RequestDemoModal3
        open={isOpenRequest}
        onClose={() => setOpenRequest(false)}
      />
    </div>
  );
};

export default TariffsItem;
